import { useEffect, useState } from 'react';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import { useSiteUserPersonalization } from '@atlassian/jira-personalization-service/src/services/site-user/index.tsx';
import { useSitePersonalization } from '@atlassian/jira-personalization-service/src/services/site/index.tsx';
import type { Trait } from '@atlassian/jira-personalization-service/src/types.tsx';
import { useQueryParam } from '@atlassian/react-resource-router';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { localStorageProvider } from '@atlassian/jira-onboarding-core/src/utils/index.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import {
	TWENTY_FOUR_HOURS_MS,
	TWENTY_FOUR_HOURS_FIVE_MINS_MS,
	IS_FOUNDER_TRAIT,
	JIRA_FIRST_ACTIVE_TRAIT,
	JSW_ACTIVATION_DATE_TRAIT,
	NTH_USER_WELCOME_MODAL_VIEWED_TRAIT,
	type SiteTraits,
	type SiteUserTraits,
	CanShowNthUserWelcomeModalReason,
} from './types.tsx';

export const validateDate = (dateString: string | undefined): Date | undefined => {
	if (!dateString) {
		return undefined;
	}

	const date = new Date(dateString);
	return Number.isNaN(date.getTime()) ? undefined : date;
};

const extractSiteTraits = (traits: Trait[] | undefined): SiteTraits => {
	if (!traits) {
		return {};
	}

	const jiraActivationDateTrait = traits.find((trait) => trait.name === JSW_ACTIVATION_DATE_TRAIT);

	return {
		jiraActivationDate: jiraActivationDateTrait?.value.toString(),
	};
};

const extractSiteUserTraits = (traits: Trait[] | undefined): SiteUserTraits => {
	if (!traits) {
		return {};
	}

	const founderTrait = traits.find((trait) => trait.name === IS_FOUNDER_TRAIT);
	const jiraFirstActiveTrait = traits.find((trait) => trait.name === JIRA_FIRST_ACTIVE_TRAIT);
	const hasViewedNthUserWelcomeModalTrait = traits.find(
		(trait) => trait.name === NTH_USER_WELCOME_MODAL_VIEWED_TRAIT,
	);

	return {
		isFounder: founderTrait?.value === true,
		jiraFirstActive: jiraFirstActiveTrait?.value.toString(),
		hasViewedNthUserWelcomeModal: hasViewedNthUserWelcomeModalTrait?.value === true,
	};
};

export const canShowNthUserWelcomeModal = (
	isFounder: boolean | undefined,
	userFirstActiveInJiraString: string | undefined,
	jiraActivationDateString: string | undefined,
	hasViewedNthUserWelcomeModal: boolean | undefined,
	shouldShowNthUserWelcomeModal: boolean,
): { result: boolean; reason: string; skipAnalytics?: boolean } => {
	if (isFounder) {
		return {
			result: false,
			reason: CanShowNthUserWelcomeModalReason.IS_FOUNDER,
			skipAnalytics: true,
		};
	}

	if (hasViewedNthUserWelcomeModal) {
		return {
			result: false,
			reason: CanShowNthUserWelcomeModalReason.HAS_VIEWED_NTH_USER_WELCOME_MODAL,
			skipAnalytics: true,
		};
	}

	if (shouldShowNthUserWelcomeModal) {
		return {
			result: true,
			reason: CanShowNthUserWelcomeModalReason.QUERY_PARAM_PRESENT,
		};
	}

	const userFirstActiveInJiraDate = validateDate(userFirstActiveInJiraString);
	const jiraActivationDate = validateDate(jiraActivationDateString);

	if (!jiraActivationDate) {
		return {
			result: false,
			reason: CanShowNthUserWelcomeModalReason.JIRA_ACTIVATION_DATE_NOT_PRESENT,
		};
	}

	if (!userFirstActiveInJiraDate) {
		return {
			result: false,
			reason: CanShowNthUserWelcomeModalReason.JIRA_FIRST_ACTIVE_NOT_PRESENT,
		};
	}

	const now = new Date();
	const twentyFourHoursAgo = new Date(now.getTime() - TWENTY_FOUR_HOURS_MS);
	const twentyFourHoursFiveMinutesAgo = new Date(now.getTime() - TWENTY_FOUR_HOURS_FIVE_MINS_MS);

	// Return true if user is first active in Jira in the last 24 hours and Jira was activated more than 24 hours and 5 minutes ago
	// This condition further reduces the chances of a founder being shown the modal, in case founder trait was not updated
	// The additional 5 minutes is to account for any potential delays between the activation date and the first active date for the founder
	if (userFirstActiveInJiraDate < twentyFourHoursAgo) {
		return {
			result: false,
			reason: CanShowNthUserWelcomeModalReason.USER_FIRST_ACTIVE_IN_JIRA_DATE_GREATER_THAN_24_HOURS,
			skipAnalytics: true,
		};
	}
	if (jiraActivationDate >= twentyFourHoursFiveMinutesAgo) {
		return {
			result: false,
			reason:
				CanShowNthUserWelcomeModalReason.JIRA_ACTIVATION_DATE_LESS_THAN_24_HOURS_AND_5_MINUTES_AGO,
		};
	}

	return {
		result: true,
		reason: CanShowNthUserWelcomeModalReason.NONE,
	};
};

export const useCanShowNthUserWelcomeModal = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const cloudId = useCloudId();
	const atlassianAccountId = useAccountId();
	const {
		fetch: fetchSiteUserPersonalization,
		loading: loadingSiteUserTraits,
		data: siteUserData,
		error: siteUserError,
	} = useSiteUserPersonalization();
	const {
		fetch: fetchSitePersonalization,
		loading: loadingSiteTraits,
		data: siteData,
		error: siteError,
	} = useSitePersonalization();

	const [siteTraits, setSiteTraits] = useState<SiteTraits | undefined>(undefined);
	const [siteUserTraits, setSiteUserTraits] = useState<SiteUserTraits | undefined>(undefined);
	const [canShow, setCanShow] = useState<boolean>(false);

	const [showNthUserWelcomeModal] = useQueryParam('showNthUserWelcomeModal');
	const shouldShowNthUserWelcomeModal = showNthUserWelcomeModal === 'true';

	useEffect(() => {
		if (!cloudId || loadingSiteTraits || siteError || siteTraits) {
			return;
		}

		if (siteData) {
			setSiteTraits(extractSiteTraits(siteData));
		} else {
			const fetchSiteTraits = async () => {
				const traits = await fetchSitePersonalization({ cloudId });
				setSiteTraits(extractSiteTraits(traits));
			};

			fetchSiteTraits();
		}
	}, [cloudId, fetchSitePersonalization, loadingSiteTraits, siteData, siteError, siteTraits]);

	useEffect(() => {
		if (
			!cloudId ||
			!atlassianAccountId ||
			loadingSiteUserTraits ||
			siteUserError ||
			siteUserTraits
		) {
			return;
		}

		if (siteUserData) {
			setSiteUserTraits(extractSiteUserTraits(siteUserData));
		} else {
			const fetchSiteUserTraits = async () => {
				const traits = await fetchSiteUserPersonalization({ cloudId, userId: atlassianAccountId });
				setSiteUserTraits(extractSiteUserTraits(traits));
			};
			fetchSiteUserTraits();
		}
	}, [
		atlassianAccountId,
		cloudId,
		fetchSiteUserPersonalization,
		loadingSiteUserTraits,
		siteUserData,
		siteUserError,
		siteUserTraits,
	]);

	useEffect(() => {
		if (siteError) {
			fireOperationalAnalytics(createAnalyticsEvent({}), 'canShowNthUserWelcomeModal evaluated', {
				reason: CanShowNthUserWelcomeModalReason.ERROR_FETCHING_SITE_TRAITS,
				result: false,
			});
			setCanShow(false);
			return;
		}

		if (siteUserError) {
			fireOperationalAnalytics(createAnalyticsEvent({}), 'canShowNthUserWelcomeModal evaluated', {
				reason: CanShowNthUserWelcomeModalReason.ERROR_FETCHING_SITE_USER_TRAITS,
				result: false,
			});
			setCanShow(false);
			return;
		}

		if (loadingSiteTraits || loadingSiteUserTraits || !siteTraits || !siteUserTraits) {
			setCanShow(false);
			return;
		}

		const hasViewedNthUserWelcomeModalLocal = localStorageProvider.get(
			NTH_USER_WELCOME_MODAL_VIEWED_TRAIT,
		);

		const { result, reason, skipAnalytics } = canShowNthUserWelcomeModal(
			siteUserTraits.isFounder,
			siteUserTraits.jiraFirstActive,
			siteTraits.jiraActivationDate,
			siteUserTraits.hasViewedNthUserWelcomeModal || hasViewedNthUserWelcomeModalLocal,
			shouldShowNthUserWelcomeModal,
		);

		if (!skipAnalytics) {
			fireOperationalAnalytics(createAnalyticsEvent({}), 'canShowNthUserWelcomeModal evaluated', {
				reason,
				result,
				timeSinceJiraFirstActive:
					siteUserTraits.jiraFirstActive && result === true
						? differenceInSeconds(new Date(), new Date(siteUserTraits.jiraFirstActive))
						: undefined,
			});
		}

		setCanShow(result);
	}, [
		shouldShowNthUserWelcomeModal,
		loadingSiteTraits,
		loadingSiteUserTraits,
		siteError,
		siteUserError,
		siteTraits,
		siteUserTraits,
		createAnalyticsEvent,
	]);

	return canShow;
};
